import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTenantAuth } from '@/auth/TenantAuthContext';
import {
  IconArrowDownCircle,
  IconArrowLeft,
  IconChartBar,
  IconChevronRight,
  IconClose,
  IconDashboard,
  IconMenu,
  IconReceipt,
  IconStore,
  IconTruck,
} from '@/components/Icons';
import { LogoMark } from '@/components/Logo';
import { API_URL } from '@/lib/api';
import { UserMenu } from '@/components/UserMenu';
import { useExitImpersonation } from '@/features/impersonation/useImpersonation';
import { PortalContextProvider } from '@/features/portal/PortalContext';
import type { PortalContextValue } from '@/features/portal/PortalContext';

interface NavItem {
  to: string;
  label: string;
  /** Top-level items only. Sub-items sit under a group header that has one. */
  icon?: ReactNode;
  end?: boolean;
  /**
   * A page made of cost figures (purchase prices, profit). Hidden — not
   * disabled — for a login whose `canViewCost` is off; the API refuses it
   * too, so the hiding is only to keep the menu honest.
   */
  cost?: boolean;
}

/** A collapsible group of report pages, mirroring the till's own side menu. */
interface NavGroup {
  label: string;
  icon: ReactNode;
  items: readonly NavItem[];
}

/**
 * Grouped to match `retail-pos`'s own `side_menu.dart` section-for-section
 * (Sales / Reports / Catalog / Finance), so an owner who knows the till's menu
 * knows where to look here without re-learning a second layout. Dashboard
 * stays on its own — it is one page, and a group of one is just a link
 * wearing a disclosure triangle.
 *
 * Purchases joined once its reports existed (purchase list, returns, debit
 * notes). User Management, People and Settings are still absent on purpose:
 * the portal is read-only reporting, and an empty group would imply data
 * that was never synced or never reported on.
 */
const NAV_DASHBOARD: NavItem = {
  to: '/',
  label: 'Dashboard',
  icon: <IconDashboard className="h-4 w-4" />,
  end: true,
};

const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Sales',
    icon: <IconReceipt className="h-4 w-4" />,
    items: [
      { to: '/sales-history', label: 'Sales history' },
      { to: '/returns', label: 'Returns & wallet' },
      { to: '/top-movers', label: 'Top & slow movers' },
      { to: '/sales-timing', label: 'Busy hours' },
      { to: '/quotations', label: 'Quotations' },
    ],
  },
  {
    label: 'Reports',
    icon: <IconChartBar className="h-4 w-4" />,
    items: [
      { to: '/sales', label: 'Sales report' },
      { to: '/daily-sales', label: 'Daily sales' },
      { to: '/day-book', label: 'Day book' },
      { to: '/shifts', label: 'Shift report' },
      { to: '/ledger', label: 'Ledger' },
      { to: '/profit-loss', label: 'Profit & loss', cost: true },
      { to: '/branch-comparison', label: 'Branch comparison' },
      { to: '/tax', label: 'Tax' },
    ],
  },
  {
    label: 'Catalog',
    icon: <IconStore className="h-4 w-4" />,
    items: [
      { to: '/inventory', label: 'Inventory' },
      { to: '/stock-alerts', label: 'Stock alerts' },
      { to: '/expenses', label: 'Expenses & stock issues' },
    ],
  },
  {
    label: 'Purchases',
    icon: <IconTruck className="h-4 w-4" />,
    items: [
      { to: '/purchases', label: 'Purchases', cost: true },
      { to: '/purchase-items', label: 'Item-wise purchases', cost: true },
      { to: '/supplier-statement', label: 'Supplier statement', cost: true },
    ],
  },
  {
    label: 'Finance',
    icon: <IconArrowDownCircle className="h-4 w-4" />,
    items: [
      { to: '/receivables', label: 'Receivables' },
      { to: '/customer-statement', label: 'Customer statement' },
      { to: '/payables', label: 'Payables' },
    ],
  },
];

/** The menu for this login: cost pages dropped, and any group left empty with them. */
function visibleGroups(canViewCost: boolean): readonly NavGroup[] {
  if (canViewCost) return NAV_GROUPS;
  return NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => !i.cost) })).filter(
    (g) => g.items.length > 0,
  );
}

/**
 * One collapsible group.
 *
 * A real <button> with aria-expanded/aria-controls, not a styled div: the
 * header is the only way to open the group, so it has to be reachable by
 * keyboard and announced as a disclosure.
 *
 * The open state is deliberately LOCAL rather than in the URL — it is a view
 * preference, not part of what the page shows.
 */
function NavGroupSection({ group, defaultOpen }: { group: NavGroup; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  // Navigating INTO a collapsed group (via a link elsewhere, back/forward, or
  // a pasted URL) has to open it. Without this the sidebar would show no
  // active item at all and the reader would have no idea where they are.
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
      >
        {group.icon}
        <span className="flex-1 text-left">{group.label}</span>
        <IconChevronRight
          className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${
            open ? 'rotate-90' : ''
          }`}
        />
      </button>

      {/* Unmounted rather than hidden when closed: a NavLink that is not on
          screen should not be tabbable. */}
      {open ? (
        <div id={panelId} className="mt-1 space-y-1">
          {group.items.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={navSubItemClass}>
              {item.label}
            </NavLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** True when [pathname] is inside [group] — drives which group opens on load. */
function groupHoldsPath(group: NavGroup, pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  return group.items.some((item) => path === item.to || path.startsWith(`${item.to}/`));
}

/**
 * Sub-items get the indent and no icon. The group header already carries the
 * icon, and a second column of them would just add noise at this depth.
 */
function navSubItemClass({ isActive }: { isActive: boolean }): string {
  const base =
    'flex items-center rounded-lg py-1.5 pl-10 pr-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white';
  return isActive
    ? `${base} bg-brand-600/[0.12] font-semibold text-brand-700`
    : `${base} text-slate-600 hover:bg-slate-100 hover:text-slate-900`;
}

function navItemClass({ isActive }: { isActive: boolean }): string {
  const base =
    'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white';
  return isActive
    ? `${base} bg-brand-600/[0.12] text-brand-700`
    : `${base} text-slate-700 hover:bg-slate-200 hover:text-slate-900`;
}

/**
 * The app bar (sign-out, impersonation exit) is product-agnostic and has been
 * here since P0. The sidebar below the tenant name is P4: grouped the same
 * way `retail-pos`'s own side menu groups its Sales/Reports/Catalog/Finance
 * sections, so an owner who already knows the till knows where to look here.
 */
export function PortalLayout() {
  const { user, logout } = useTenantAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const exitImpersonation = useExitImpersonation();

  const profile = user ?? null;

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = useCallback(() => {
    setSigningOut(true);
    void logout().finally(() => {
      setSigningOut(false);
      navigate('/login', { replace: true });
    });
  }, [logout, navigate]);

  const contextValue = useMemo<PortalContextValue>(() => ({ profile }), [profile]);

  const sidebar = (
    <div className="flex h-full flex-col bg-white">
      <div className="flex items-center gap-2.5 px-5 py-5">
        {/* The shop's own logo when it has one, falling back to SelloKart's
            mark. `object-contain` on a white tile, not `object-cover`: a
            wordmark cropped to fill a square is worse branding than one that
            simply sits inside it. */}
        {profile?.tenantLogoUrl ? (
          <img
            src={`${API_URL}${profile.tenantLogoUrl}`}
            alt=""
            className="h-9 w-9 shrink-0 rounded-lg object-contain"
          />
        ) : (
          <LogoMark className="h-9 w-9" />
        )}
        <div className="min-w-0">
          <p
            className="truncate text-sm font-semibold text-slate-900"
            title={profile?.tenantName ?? undefined}
          >
            {profile?.tenantName ?? 'SelloKart'}
          </p>
          <p className="truncate text-xs text-slate-500">Reports</p>
        </div>
      </div>

      <nav aria-label="Reports" className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
        <NavLink to={NAV_DASHBOARD.to} end className={navItemClass}>
          {NAV_DASHBOARD.icon}
          {NAV_DASHBOARD.label}
        </NavLink>

        {visibleGroups(profile?.canViewCost !== false).map((group) => (
          <NavGroupSection
            key={group.label}
            group={group}
            // Opens itself when the current page is inside it, so a reload or
            // a shared deep link never lands you in a collapsed menu with no
            // clue where you are.
            defaultOpen={groupHoldsPath(group, location.pathname)}
          />
        ))}
      </nav>

      {/* Account + Sign out live in the top app bar — see <UserMenu>. */}
    </div>
  );

  return (
    <PortalContextProvider value={contextValue}>
      <div className="min-h-screen bg-surface">
        {/* Desktop sidebar */}
        <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-slate-200 md:block">
          {sidebar}
        </aside>

        {/* Mobile drawer */}
        {menuOpen ? (
          <div className="fixed inset-0 z-40 md:hidden">
            <div
              className="absolute inset-0 animate-fade-in bg-slate-900/50"
              onClick={() => setMenuOpen(false)}
            />
            <div className="absolute inset-y-0 left-0 w-64 shadow-xl">
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="btn btn-ghost absolute right-2 top-3 h-8 w-8 p-0 text-slate-500 hover:bg-slate-200 hover:text-slate-900"
                aria-label="Close menu"
              >
                <IconClose className="h-4 w-4" />
              </button>
              {sidebar}
            </div>
          </div>
        ) : null}

        <div className="md:pl-64">
          {/* App bar: drawer toggle + wordmark on mobile, account menu always. */}
          <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="btn btn-ghost h-9 w-9 p-0 md:hidden"
              aria-label="Open menu"
              aria-expanded={menuOpen}
            >
              <IconMenu className="h-5 w-5" />
            </button>
            <LogoMark className="h-7 w-7 md:hidden" />
            <span className="truncate text-sm font-semibold text-slate-900 md:hidden">
              {profile?.tenantName ?? 'Reports'}
            </span>

            {/* Impersonation lives in the app bar, beside the account it is
                impersonating, rather than as a banner over the page. */}
            {profile?.impersonated === true ? (
              <div
                role="status"
                className="ml-auto flex min-w-0 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 py-1 pl-3 pr-1"
              >
                <span className="hidden text-xs font-medium text-amber-900 sm:inline">
                  Viewing as
                </span>
                <span
                  className="max-w-[9rem] truncate text-xs font-semibold text-amber-900"
                  title={profile.tenantName}
                >
                  {profile.tenantName}
                </span>
                {/* Clears the TENANT session only — the SA session is a
                    separate store, so this lands back on /cp still signed in. */}
                <button
                  type="button"
                  onClick={exitImpersonation}
                  aria-label={`Exit viewing as ${profile.tenantName} and return to the admin console`}
                  className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-amber-900 transition-colors hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
                >
                  <IconArrowLeft className="h-3.5 w-3.5" />
                  Exit
                </button>
              </div>
            ) : null}

            <div className={profile?.impersonated === true ? '' : 'ml-auto'}>
              <UserMenu
                name={profile?.username ?? 'Signed in'}
                email={profile?.email}
                // Which shop these reports belong to — worth showing here
                // because a super admin can be viewing as any tenant.
                context={profile?.tenantName}
                onSignOut={handleLogout}
                signingOut={signingOut}
              />
            </div>
          </div>

          <main className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            <Outlet />
          </main>
        </div>
      </div>
    </PortalContextProvider>
  );
}
